import type { VisibilityScopeValue } from "../../../components/ui/VisibilityScope";
import type { ScrewFamily, ScrewFamilyForSettings } from "./screwFamilies";

export const CONNECTOR_FAMILY_CODE_TVM = "tvm";
export const CONNECTOR_FAMILY_CODE_FLAT_FLIP = "flat_flip";
export const CONNECTOR_FAMILY_CODE_GAP = "gap";
export const CONNECTOR_FAMILY_CODE_SNAP = "snap";
export const CONNECTOR_FAMILY_CODE_TERRALOCK = "terralock";
export const CONNECTOR_FAMILY_CODE_TERRALOCK_A2 = "terralock_a2";
export const CONNECTOR_FAMILY_CODE_TERRALOCK_PP = "terralock_pp";
export const CONNECTOR_FAMILY_CODE_KKT_COLOR = "kkt_color";
export const CONNECTOR_FAMILY_CODE_KKT_A4_AISI316 = "kkt_a4_aisi316";
export const CONNECTOR_FAMILY_CODE_KKA_COLOR = "kka_color";
export const CONNECTOR_FAMILY_CODE_KKA_AISI410 = "kka_aisi410";

export const CONNECTOR_FAMILY_CODES = [
  CONNECTOR_FAMILY_CODE_TVM,
  CONNECTOR_FAMILY_CODE_FLAT_FLIP,
  CONNECTOR_FAMILY_CODE_GAP,
  CONNECTOR_FAMILY_CODE_SNAP,
  CONNECTOR_FAMILY_CODE_TERRALOCK,
  CONNECTOR_FAMILY_CODE_TERRALOCK_A2,
  CONNECTOR_FAMILY_CODE_TERRALOCK_PP,
  CONNECTOR_FAMILY_CODE_KKT_COLOR,
  CONNECTOR_FAMILY_CODE_KKT_A4_AISI316,
  CONNECTOR_FAMILY_CODE_KKA_COLOR,
  CONNECTOR_FAMILY_CODE_KKA_AISI410,
] as const;

export type ConnectorFamilyCode = (typeof CONNECTOR_FAMILY_CODES)[number];

// The three TERRALOCK material variants (plain, A2, PP/Nylon) share the same physical connector
// shape/geometry - only the material (and its screw family compatibility) differs.
export const TERRALOCK_FAMILY_CODES: readonly string[] = [
  CONNECTOR_FAMILY_CODE_TERRALOCK,
  CONNECTOR_FAMILY_CODE_TERRALOCK_A2,
  CONNECTOR_FAMILY_CODE_TERRALOCK_PP,
];

export type ConnectorFamily = {
  code: string;
  name: string;
  nameIt: string | null;
  nameDe: string | null;
  nameFr: string | null;
  nameEs: string | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionFr: string | null;
  descriptionEs: string | null;
  groovingMinHeight: number | null;
  groovingMaxHeight: number | null;
  groovingThickness: number | null;
  isScrew: boolean;
  requiresBoardScrew: boolean;
  maxBoardThickness: number | null;
  screwFamilies: ScrewFamily[];
  visibility: VisibilityScopeValue;
  order: number;
};

// User side (settings, saved configuration): name and description already in the user's language.
export type ConnectorFamilyForSettings = Omit<
  ConnectorFamily,
  | "nameIt"
  | "nameDe"
  | "nameFr"
  | "nameEs"
  | "descriptionIt"
  | "descriptionDe"
  | "descriptionFr"
  | "descriptionEs"
  | "screwFamilies"
  | "visibility"
  | "order"
> & {
  // Settings only: set when the item is sent only because the calculation uses it (hidden since).
  onlyForCalculation?: boolean;
  screwFamilies: ScrewFamilyForSettings[];
};

export type SearchConnectorFamiliesResponse = {
  items: ConnectorFamily[];
  total: number;
};

export type UpdateConnectorFamilyBody = {
  name: string;
  nameIt: string | null;
  nameDe: string | null;
  nameFr: string | null;
  nameEs: string | null;
  description: string | null;
  descriptionIt: string | null;
  descriptionDe: string | null;
  descriptionFr: string | null;
  descriptionEs: string | null;
  groovingMinHeight: number | null;
  groovingMaxHeight: number | null;
  groovingThickness: number | null;
  isScrew: boolean;
  requiresBoardScrew: boolean;
  maxBoardThickness: number | null;
  screwFamilies: ScrewFamily[];
};
